/**
 * Act: every action Orgus can take, as data (DesignSystem 5.1). Built from live state when
 * the palette opens, ranked by the words typed and by the page you are on, and run through
 * one guarded path (./run.ts): low risk runs, reversible medium risk runs with Undo, the rest
 * confirm, and high risk asks for the step-up. The hub still applies policy to every action.
 */
import type { ActionKind, Checkout, Id, Job, Machine, Pipeline, Project, Session } from '@orgus/protocol';
import { machineLiveness, pipelineKey } from '@orgus/protocol';
import type { RiskLevel } from '@orgus/ui';
import { baseName, plural } from '@orgus/ui';

export interface ActStep {
  machineId: Id;
  kind: ActionKind;
  params: Record<string, unknown>;
}

export interface ActAction {
  /** Stable: 'session.stop:<id>', 'pipeline.pause:<key>', 'machine.stop-all:<id>'. */
  id: string;
  /** The verb, for matching and the confirm button: 'Stop', 'Pause'. */
  verb: string;
  title: string;
  /** Extra words people may type: names, machine, project. */
  keywords: string[];
  /** Every target, one line each (the confirm lists them). */
  scope: string[];
  /** One line: "1 session on studio", "4 sessions in Qawl-Agents". */
  scopeLabel: string;
  risk: RiskLevel;
  /** True when an inverse action exists (pause and resume): runs with an Undo toast. */
  reversible: boolean;
  /** Ids of the entities it touches, for context ranking (session, machine, project, pipeline key). */
  refs: string[];
  /** Which page it belongs to, for context ranking. */
  area: 'sessions' | 'machines' | 'pipelines' | 'projects' | 'general';
  /** The machine actions to send (through POST /api/actions, so policy and audit apply). */
  steps: ActStep[];
  /** Steps that undo it, when reversible. */
  undo?: ActStep[];
  /** Past tense for the result toast: 'Stopped 3 sessions'. */
  done: string;
  /** What happens, for the confirm dialog. */
  consequence: string;
  /** Runs instead of `steps` (for calls that are not machine actions, like revoking a machine). */
  custom?: () => Promise<void>;
}

export interface ActState {
  sessions: Record<Id, Session>;
  machines: Record<Id, Machine>;
  projects: Record<Id, Project>;
  checkouts: Record<Id, Checkout>;
  pipelines: Record<string, Pipeline>;
  jobs: Record<Id, Job>;
  now: number;
}

function clip(s: string, n = 48): string {
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s;
}

export function stopSessionsAction(list: Session[], st: Pick<ActState, 'machines' | 'projects'>, id: string, label?: string): ActAction {
  const live = list.filter((s) => s.status !== 'ended');
  const machinesTouched = [...new Set(live.map((s) => st.machines[s.machineId]?.name ?? 'unknown machine'))];
  const where = machinesTouched.length === 1 ? ` on ${machinesTouched[0]}` : ` on ${plural(machinesTouched.length, 'machine')}`;
  const one = live.length === 1 ? live[0]! : null;
  const subagents = live.reduce((a, s) => a + s.subagents.filter((x) => x.status === 'running').length, 0);
  return {
    id,
    verb: 'Stop',
    title: label ?? (one ? `Stop session "${clip(one.title)}"` : `Stop ${plural(live.length, 'session')}`),
    keywords: [...new Set(live.flatMap((s) => [st.machines[s.machineId]?.name ?? '', st.projects[s.projectId ?? '']?.name ?? '']))].filter(Boolean),
    scope: live.map((s) => `${clip(s.title, 60)} (${st.machines[s.machineId]?.name ?? 'unknown machine'})`),
    scopeLabel: `${plural(live.length, 'session')}${where}`,
    risk: 'high',
    reversible: false,
    refs: [...live.map((s) => s.id), ...new Set(live.map((s) => s.machineId))],
    area: 'sessions',
    steps: live.map((s) => ({ machineId: s.machineId, kind: 'session.stop', params: { sessionId: s.id } })),
    done: one ? 'Stopped the session' : `Stopped ${plural(live.length, 'session')}`,
    consequence: `Claude Code ends in ${live.length === 1 ? 'that session' : `each of them`}${subagents ? `, with ${plural(subagents, 'running subagent')}` : ''}. Transcripts stay; work in progress stops and cannot be resumed from here.`,
  };
}

/** Remove clean worktrees (one or many): high risk, the folders are deleted. */
export function removeWorktreesAction(list: Checkout[], st: Pick<ActState, 'machines' | 'projects'>, id: string): ActAction {
  const one = list.length === 1 ? list[0]! : null;
  const machinesTouched = [...new Set(list.map((c) => st.machines[c.machineId]?.name ?? 'unknown machine'))];
  const where = machinesTouched.length === 1 ? ` on ${machinesTouched[0]}` : ` on ${plural(machinesTouched.length, 'machine')}`;
  const ahead = list.filter((c) => c.ahead > 0).length;
  return {
    id,
    verb: 'Remove',
    title: one ? `Remove worktree ${baseName(one.path)} (${st.projects[one.projectId]?.name ?? 'unknown project'})` : `Remove ${plural(list.length, 'worktree')}`,
    keywords: [...list.map((c) => baseName(c.path)), ...list.map((c) => c.branch ?? ''), ...machinesTouched, 'worktree', 'delete'].filter(Boolean),
    scope: list.map((c) => `${c.path} on ${st.machines[c.machineId]?.name ?? 'unknown machine'}`),
    scopeLabel: `${plural(list.length, 'worktree')}${where}`,
    risk: 'high',
    reversible: false,
    refs: [...list.map((c) => c.id), ...new Set(list.map((c) => c.projectId)), ...new Set(list.map((c) => c.machineId))],
    area: 'projects',
    steps: list.map((c) => ({ machineId: c.machineId, kind: 'worktree.remove', params: { checkoutId: c.id } })),
    done: one ? `Removed worktree ${baseName(one.path)}` : `Removed ${plural(list.length, 'worktree')}`,
    consequence: `The folders and their git worktree entries are deleted. Branches stay${ahead ? `, including ${plural(ahead, 'branch', 'branches')} with commits not on the main branch` : ''}.`,
  };
}

/** Every action available now. Cheap enough to build on each palette open. */
export function buildActions(st: ActState): ActAction[] {
  const out: ActAction[] = [];
  const mName = (id: Id) => st.machines[id]?.name ?? 'unknown machine';
  const pName = (id: Id | null) => (id ? (st.projects[id]?.name ?? 'unknown project') : 'no project');
  const online = (id: Id) => {
    const m = st.machines[id];
    return !!m && machineLiveness(m, st.now) !== 'offline';
  };

  // Sessions: one by one, then per machine and per project.
  const live = Object.values(st.sessions).filter((s) => s.status !== 'ended' && online(s.machineId));
  live.sort((a, b) => b.lastActivity - a.lastActivity);
  for (const s of live) {
    const a = stopSessionsAction([s], st, `session.stop:${s.id}`);
    a.keywords.push(s.id.slice(0, 8));
    out.push(a);
  }
  const byMachine = new Map<Id, Session[]>();
  const byProject = new Map<Id, Session[]>();
  for (const s of live) {
    byMachine.set(s.machineId, [...(byMachine.get(s.machineId) ?? []), s]);
    if (s.projectId) byProject.set(s.projectId, [...(byProject.get(s.projectId) ?? []), s]);
  }
  for (const [mid, list] of byMachine) {
    if (list.length < 2) continue;
    out.push(stopSessionsAction(list, st, `machine.stop-all:${mid}`, `Stop every session on ${mName(mid)}`));
    const waiting = list.filter((s) => s.status === 'waiting');
    if (waiting.length > 1) out.push(stopSessionsAction(waiting, st, `machine.stop-waiting:${mid}`, `Stop the ${waiting.length} waiting sessions on ${mName(mid)}`));
  }
  for (const [pid, list] of byProject) {
    if (list.length < 2) continue;
    out.push(stopSessionsAction(list, st, `project.stop-all:${pid}`, `Stop every session in ${pName(pid)}`));
  }

  // Pipelines: pause and resume undo each other; restart confirms.
  for (const p of Object.values(st.pipelines)) {
    const key = pipelineKey(p);
    const label = `${pName(p.projectId)} ${p.title.toLowerCase()} on ${mName(p.machineId)}`;
    const base = { keywords: [pName(p.projectId), mName(p.machineId), p.title], refs: [key, p.projectId, p.machineId], area: 'pipelines' as const, scope: [label], scopeLabel: `1 pipeline on ${mName(p.machineId)}` };
    const step = (kind: ActionKind): ActStep => ({ machineId: p.machineId, kind, params: { projectId: p.projectId } });
    if (!online(p.machineId)) continue;
    if (p.actions.includes('pipeline.pause') && p.state !== 'paused') {
      out.push({
        ...base,
        id: `pipeline.pause:${key}`,
        verb: 'Pause',
        title: `Pause ${label}`,
        risk: 'medium',
        reversible: p.actions.includes('pipeline.resume'),
        steps: [step('pipeline.pause')],
        undo: p.actions.includes('pipeline.resume') ? [step('pipeline.resume')] : undefined,
        done: `Paused the ${pName(p.projectId)} pipeline`,
        consequence: 'Nothing new starts; what is running finishes.',
      });
    }
    if (p.actions.includes('pipeline.resume') && p.state === 'paused') {
      out.push({
        ...base,
        id: `pipeline.resume:${key}`,
        verb: 'Resume',
        title: `Resume ${label}`,
        risk: 'low',
        reversible: p.actions.includes('pipeline.pause'),
        steps: [step('pipeline.resume')],
        undo: p.actions.includes('pipeline.pause') ? [step('pipeline.pause')] : undefined,
        done: `Resumed the ${pName(p.projectId)} pipeline`,
        consequence: 'Queued items start again.',
      });
    }
    if (p.actions.includes('pipeline.restart')) {
      out.push({
        ...base,
        id: `pipeline.restart:${key}`,
        verb: 'Restart',
        title: `Restart ${label}`,
        risk: 'high',
        reversible: false,
        steps: [step('pipeline.restart')],
        done: `Restarted the ${pName(p.projectId)} pipeline`,
        consequence: 'Items in flight are interrupted and run again from the start.',
      });
    }
  }

  // Jobs.
  for (const j of Object.values(st.jobs)) {
    if (j.state !== 'running' || !online(j.machineId)) continue;
    out.push({
      id: `job.cancel:${j.id}`,
      verb: 'Cancel',
      title: `Cancel ${clip(j.command, 40)} on ${mName(j.machineId)}`,
      keywords: [mName(j.machineId), j.command, 'job', 'command'],
      scope: [`${j.command} in ${j.cwd}`],
      scopeLabel: `1 command on ${mName(j.machineId)}`,
      risk: 'medium',
      reversible: false,
      refs: [j.id, j.machineId],
      area: 'machines',
      steps: [{ machineId: j.machineId, kind: 'job.cancel', params: { jobId: j.id } }],
      done: 'Cancelled the command',
      consequence: 'The command is stopped where it is. Its output so far stays.',
    });
  }

  // Worktrees that can go: clean, and no live session inside.
  const busyCheckouts = new Set(live.map((s) => s.checkoutId).filter(Boolean));
  for (const c of Object.values(st.checkouts)) {
    if (c.kind !== 'worktree' || c.dirty > 0 || busyCheckouts.has(c.id) || !online(c.machineId)) continue;
    const a = removeWorktreesAction([c], st, `worktree.remove:${c.id}`);
    a.keywords.push(pName(c.projectId));
    out.push(a);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

export interface ActContext {
  /** The route path: '/sessions'. */
  path: string;
  /** Ids in the address or selected on the page (open session, machine, pipeline). */
  focus: string[];
}

const AREA_PATH: Record<ActAction['area'], string> = {
  sessions: '/sessions',
  machines: '/machines',
  pipelines: '/pipelines',
  projects: '/projects',
  general: '',
};

/**
 * Score an action against typed words: every word must prefix-match a word of the title or
 * keywords. The verb first scores higher; the page you are on and the item in focus boost.
 * Returns 0 for no match.
 */
export function scoreAct(a: ActAction, words: string[], ctx: ActContext): number {
  const hayWords = `${a.title} ${a.keywords.join(' ')}`.toLowerCase().split(/[^a-z0-9.-]+/).filter(Boolean);
  let s = 0;
  for (const w of words) {
    const hit = hayWords.some((h) => h.startsWith(w)) || `${a.title} ${a.keywords.join(' ')}`.toLowerCase().includes(w);
    if (!hit) return 0;
    s += 1;
  }
  if (words.length && a.verb.toLowerCase().startsWith(words[0]!)) s += 0.5;
  if (ctx.focus.some((f) => a.refs.includes(f))) s += 2;
  if (AREA_PATH[a.area] && ctx.path.startsWith(AREA_PATH[a.area])) s += 0.4;
  // Single-target actions read more precisely than "every session on ..." when equal.
  if (a.steps.length > 1) s -= 0.1;
  return s;
}

/** The best actions for the words typed (or, with none, those about what is in focus). */
export function matchActs(query: string, actions: ActAction[], ctx: ActContext, limit = 8): { action: ActAction; score: number }[] {
  const words = query.toLowerCase().replace(/^[>\s]+/, '').split(/\s+/).filter(Boolean);
  const scored: { action: ActAction; score: number }[] = [];
  for (const a of actions) {
    const score = words.length ? scoreAct(a, words, ctx) : ctx.focus.some((f) => a.refs.includes(f)) ? 2 : 0;
    if (score > 0) scored.push({ action: a, score });
  }
  scored.sort((x, y) => y.score - x.score || x.action.title.localeCompare(y.action.title));
  return scored.slice(0, limit);
}
